/**
 * Use full width - compact cards for content that warrants the highest level of prominence and you want a full width image (starting and ending with the column grid) that has a shortened height.
 *
 * @module views/components/HeroFullWidthCompact
 * @memberof -Common
 */
import './HeroFullWidthCompact.scss';

import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Text from '@ulta/core/components/Text/Text';
import UltaAsset from '@ulta/core/components/UltaAsset/UltaAsset';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { constants } from '@ulta/core/utils/constants/constants';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import Link from '@ulta/modules/Link/Link';
import Tag from '@ulta/modules/Tag/Tag';

/**
* Represents a HeroFullWidthCompact component
*
* @method
* @param {HeroFullWidthCompactProps} props - React properties passed from composition
* @returns HeroFullWidthCompact
*/
export const HeroFullWidthCompact = function( props ){
  const { breakpoint } = useDeviceInflection();
  const {
    desktopImage,
    tabletImage,
    mobileImage,
    textLayout,
    textColor,
    tag,
    eyebrow,
    headline,
    headlineTag = defaultProps.headlineTag,
    subheadline,
    secondarySubheadline,
    linkAction,
    root = defaultProps.root,
    rootMargin = defaultProps.rootMargin,
    threshold = defaultProps.threshold
  } = props;
  const heroFullWidthRef = useRef( null );
  const isMobile = breakpoint?.isSmallDevice();
  const isDesktop = breakpoint && ( breakpoint.CURRENT_BREAKPOINT === 'LG' || breakpoint.CURRENT_BREAKPOINT === 'XL' );
  const isTablet = breakpoint && ( breakpoint.CURRENT_BREAKPOINT === 'MD' );
  const textAlignment = ( textLayout === 'left-aligned' ? 'left' : 'center' );
  useIntersectionObserver( heroFullWidthRef, {
    root: root,
    rootMargin: rootMargin,
    threshold: threshold
  }, handleIntersection( props ) );

  if( !desktopImage || !tabletImage || !mobileImage || !headline || !textLayout ){
    return null;
  }

  return (
    <div className='HeroFullWidthCompact'
      ref={ heroFullWidthRef }
    >
      { desktopImage &&
      <div className='HeroFullWidthCompact__backgroundImageContainer'>
        <GridContainer>
          <div className='HeroFullWidthCompact__backgroundImage'>
            { isDesktop &&
              <UltaAsset
                { ...spreadProps( { ...desktopImage } ) }
                bgAnimation={ true }
              />
            }
            { isTablet &&
              <UltaAsset
                { ...spreadProps( { ...tabletImage } ) }
              />
            }
            { isMobile &&
              <UltaAsset
                { ...spreadProps( { ...mobileImage } ) }
              />
            }
          </div>
        </GridContainer>
      </div>
      }
      <div className={
        classNames( 'HeroFullWidthCompact__wrapper', {
          [`HeroFullWidthCompact__wrapper--${textLayout}`]:textLayout
        } )
      }
      >
        <div className='HeroFullWidthCompact__content'>
          <div className={
            classNames( 'HeroFullWidthCompact__textSection', {
              [`HeroFullWidthCompact__textSection--${textColor}`]:textColor
            } )
          }
          >
            { tag && !eyebrow &&
              <div className='HeroFullWidthCompact__tag'>
                <Tag
                  variant='default'
                  style='white'
                  message={ tag }
                />
              </div>
            }
            { eyebrow &&
              <div className='HeroFullWidthCompact__eyebrow'>
                <Text
                  htmlTag='p'
                  textStyle='eyebrow'
                  textAlign={ textAlignment }
                  color={ textColor }
                >
                  { eyebrow }
                </Text>
              </div>
            }
            { headline &&
              <div className={
                classNames( 'HeroFullWidthCompact__headline', {
                  'HeroFullWidthCompact__headline--tagNoEyebrow': tag && !eyebrow
                } )
              }
              >
                <Text
                  htmlTag={ headlineTag }
                  textStyle='title-3'
                  textAlign={ textAlignment }
                  color={ textColor }
                >
                  { headline }
                </Text>
              </div>
            }
            { subheadline &&
              <div className='HeroFullWidthCompact__subheadline'>
                <Text
                  htmlTag='p'
                  textStyle='subtitle-1'
                  textAlign={ textAlignment }
                  color={ textColor }
                >
                  { subheadline }
                </Text>
              </div>
            }
            { secondarySubheadline &&
              <div className='HeroFullWidthCompact__secondarySubheadline'>
                <Text
                  htmlTag='p'
                  textStyle='subtitle-1'
                  textAlign={ textAlignment }
                  color={ textColor }
                >
                  { secondarySubheadline }
                </Text>
              </div>
            }
            { linkAction.label &&
              <div className='HeroFullWidthCompact__action'>
                <Link
                  linkAction={ linkAction }
                  linkIcon='ArrowForward'
                  linkStyle='title-6'
                  linkColor={ textColor }
                />
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  );
};

/**
* Property type definitions
* @typedef HeroFullWidthCompactProps
* @type {object}
* @property {object} desktopImage - Sets the backgroundImage
* @property {object} mobileImage - Sets the background Image for mobile view
* @property {object} tabletImage - Sets the campaignImage logo
* @property {string} tag - Sets the tag
* @property {string} eyebrow - Sets the eyebrow text
* @property {string} headline - Sets the headline
* @property {string} headlineTag - Sets the headline
* @property {string} textLayout - Sets the headline
* @property {string} textColor - Sets the headline
* @property {string} subheadline - Sets the subheadline
* @property {string} secondarySubheadline - Sets the secondarySubheadline
* @property {object} linkAction - Sets the action
*/
export const propTypes =  {
  desktopImage: PropTypes.object,
  mobileImage: PropTypes.object,
  tabletImage: PropTypes.object,
  tag: PropTypes.string,
  eyebrow: PropTypes.string,
  headline: PropTypes.string,
  headlineTag: PropTypes.string,
  textLayout: PropTypes.string,
  textColor: PropTypes.string,
  subheadline: PropTypes.string,
  secondarySubheadline: PropTypes.string,
  linkAction: PropTypes.object
};

/**
* Default values for passed properties
* @type object
* @property {string} headlineTag='h2' - Default headlineTag is h2
*/
export const defaultProps = {
  headlineTag: 'h2',
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

HeroFullWidthCompact.defaultProps = defaultProps;
HeroFullWidthCompact.propTypes = propTypes;

export default HeroFullWidthCompact;